import { useState } from 'react';
import { TaskPage } from '../../../components/workbook/TaskPage';
import { Checkbox } from '../../../components/workbook/forms';
import { Callout } from '../../../components/workbook/Callout';

interface Filter {
  id: string;
  criterion: string;
  applied: boolean;
}

export default function FilterFocusPage() {
  const [filters, setFilters] = useState<Filter[]>([
    { id: '1', criterion: 'Value to current goals (H/M/L)', applied: false },
    { id: '2', criterion: 'Time this week (0-30m / 30-120m / 2h+)', applied: false },
    { id: '3', criterion: 'Must be done this cycle', applied: false },
    { id: '4', criterion: 'Suspect items (uncertain value)', applied: false },
  ]);

  const [customFilter, setCustomFilter] = useState('');

  const toggleFilter = (id: string) => {
    setFilters(filters.map(f => 
      f.id === id ? { ...f, applied: !f.applied } : f
    ));
  };

  const addCustomFilter = () => {
    if (customFilter.trim()) {
      setFilters([...filters, { id: Date.now().toString(), criterion: customFilter, applied: false }]);
      setCustomFilter('');
    }
  };

  const appliedCount = filters.filter(f => f.applied).length;

  return (
    <TaskPage
      phase="decide"
      title="Filter & Focus"
      pageNumber={3}
      instruction="Apply filters to narrow your scope. Use criteria from your inventory to focus on what matters most."
      whyItMatters="Not everything needs prioritization. Filters help you focus on the right items and avoid wasting time on the wrong ones."
      coachRail={{
        timing: "8 minutes",
        tips: "Start with 1-2 filters, add more if needed. Too many filters can eliminate everything.",
        debrief: "How many items passed your filters? Do the filters feel right?"
      }}
    >
      <div className="space-y-6">
        {/* Filter Criteria */}
        <div className="bg-[var(--surface)] p-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">
            Filter Criteria
          </h3>
          <div className="space-y-3">
            {filters.map((filter) => (
              <label key={filter.id} className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={filter.applied}
                  onChange={() => toggleFilter(filter.id)}
                  label=""
                />
                <div className="flex-1">
                  <span className="text-[var(--text-sec)]">{filter.criterion}</span>
                </div>
              </label>
            ))}
            
            <div className="flex gap-2 mt-4">
              <input
                type="text"
                value={customFilter}
                onChange={(e) => setCustomFilter(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addCustomFilter()}
                placeholder="Add custom filter..."
                className="flex-1 px-3 py-2 border border-[var(--rule)] text-sm"
              />
              <button
                onClick={addCustomFilter}
                className="px-4 py-2 bg-[var(--green)] text-white text-sm hover:bg-[var(--green)]"
              >
                Add
              </button>
            </div>
          </div>
        </div>

        {/* Filter Summary */}
        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-4">
          <h3 className="font-semibold text-[var(--text-sec)] mb-2">
            Active Filters
          </h3>
          <div className="text-[var(--text-sec)]">
            {appliedCount === 0 ? (
              <p className="text-sm">No filters applied yet. Select filters above to narrow your scope.</p>
            ) : (
              <div className="space-y-1">
                {filters.filter(f => f.applied).map(filter => (
                  <p key={filter.id} className="text-sm">• {filter.criterion}</p>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Application Guidance */}
        <Callout type="tip" phase="decide">
          <div className="space-y-2">
            <p className="font-semibold">How to Apply Filters:</p>
            <ul className="text-sm space-y-1">
              <li>• Review your Priority Inventory</li>
              <li>• Apply selected filters to each item</li>
              <li>• Items that pass all filters proceed</li>
              <li>• Items that fail go to backlog or need refinement</li>
            </ul>
          </div>
        </Callout>

        {/* Common Filters */}
        <div className="bg-[var(--surface)] border border-[var(--rule)] p-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">
            Common Filter Patterns
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-[var(--text-sec)]">
            <div>
              <p className="font-medium mb-2">By Value:</p>
              <ul className="space-y-1">
                <li>• High value only</li>
                <li>• Medium+ value</li>
                <li>• Strategic alignment</li>
              </ul>
            </div>
            <div>
              <p className="font-medium mb-2">By Time:</p>
              <ul className="space-y-1">
                <li>• Under 2 hours this week</li>
                <li>• Quick wins (≤30 minutes)</li>
                <li>• Time-bound commitments</li>
              </ul>
            </div>
            <div>
              <p className="font-medium mb-2">By Urgency:</p>
              <ul className="space-y-1">
                <li>• Must complete this cycle</li>
                <li>• Deadline-driven</li>
                <li>• Blocking others</li>
              </ul>
            </div>
            <div>
              <p className="font-medium mb-2">By Quality:</p>
              <ul className="space-y-1">
                <li>• Remove suspect items</li>
                <li>• Clear outcome defined</li>
                <li>• Owner identified</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Reflection */}
        <div className="bg-[var(--green-dim)] border border-[var(--green)] p-6">
          <h3 className="text-lg font-semibold text-[var(--green)] dark:text-[var(--text-ter)] mb-4">
            Reflection Questions
          </h3>
          <div className="space-y-3 text-[var(--green)] dark:text-[var(--text-ter)]">
            <p>• Which filters are most useful for your context?</p>
            <p>• What items are being filtered out? Should they be?</p>
            <p>• Are your filters too strict or too loose?</p>
            <p>• What patterns do you see in filtered items?</p>
          </div>
        </div>
      </div>
    </TaskPage>
  );
}






